import { useField } from 'formik';
import { useState, useEffect, useRef } from 'react';

import { Avatar, Badge, Box, IconButton, Typography } from '@mui/material';
import { EditRounded, PhotoCamera as PhotoCameraIcon } from '@mui/icons-material';
import imageStreamReaderAsync from 'utils/imageStreamReaderAsync';
import getBase64String from 'utils/getBase64String';
import { useModal } from 'hooks/useModal';

import { PictureManagerModal } from './PictureManagerModal';
import { Props } from './types';
import { avatarIconStyles, avatarStyles, badgeStyles } from './styles';
import { UserProfilePicture } from 'types/user';
import { DEFAULT_USER_PROFILE_IMAGE } from 'pages/constants/user';

export const AvatarUploader = ({ image, defaultImage, name }: Props) => {
    const [field, meta, helpers] = useField(name);
    const [imageInfo, setImage] = useState<UserProfilePicture>(image);

    const { open, openModal, closeModal } = useModal();

    const fileInputRef = useRef<HTMLInputElement>(null);

    const fileInputClickHandler = () => {
        if (fileInputRef && fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const imageUpload = (e: any) => {
        e.preventDefault();
        const file = e.target.files[0];
        if (file.size > 5242880) {
            helpers.setError('Exceeded size limit');
            return;
        }
        imageStreamReaderAsync(file).then((image) => {
            const base64 = getBase64String(image as string);
            const updatedImage = {
                image: base64,
                fileName: file.name || 'user_picture',
            };
            setImage({ ...updatedImage, image: image as string });
            field.onChange({ target: { name, value: updatedImage } });
        });
    };

    const deletePictureHandler = () => {
        setImage(DEFAULT_USER_PROFILE_IMAGE);
        field.onChange({ target: { name, value: DEFAULT_USER_PROFILE_IMAGE } });
    };

    useEffect(() => {
        setImage(image);
    }, [image]);

    return (
        <>
            <IconButton aria-label="cart" onClick={openModal}>
                <Badge
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    badgeContent={
                        imageInfo.image ? (
                            <EditRounded sx={{ fontSize: 20 }} />
                        ) : (
                            <PhotoCameraIcon sx={{ fontSize: 20 }} />
                        )
                    }
                    overlap="circular"
                    sx={badgeStyles}
                >
                    <Avatar src={imageInfo.image} sx={avatarStyles}>
                        {defaultImage || <PhotoCameraIcon sx={avatarIconStyles} />}
                    </Avatar>
                </Badge>
            </IconButton>
            {meta.error && (
                <Typography color="error" variant="body2">
                    {meta.error}
                </Typography>
            )}
            <Box
                component="input"
                ref={fileInputRef}
                accept=".jpeg, .png, .jpg"
                type="file"
                display="none"
                onChange={imageUpload}
            />
            <PictureManagerModal
                isOpen={open}
                onClose={closeModal}
                image={imageInfo.image}
                defaultImage={defaultImage}
                onAdd={fileInputClickHandler}
                onDelete={deletePictureHandler}
            />
        </>
    );
};
